import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, BadgeCheck, CreditCard, ExternalLink, RefreshCw, ShieldCheck,
  ShieldAlert, Users, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { authedFetch, errText } from "../lib/api";
import { supabase } from "../lib/supabaseClient";
import { LaunchStatusBanner } from "../components/LaunchStatusBanner";
import { SRMark } from "../components/SRMark";
import { AVAIL_OPTIONS } from "../lib/capacity";
import Clinicians from "./Clinicians";

const ROLE_CONFIG = {
  gp: { label: "GP", audience: "gp" },
  psychologist: { label: "Psychologist", audience: "psychologist" },
  psychiatrist: { label: "Psychiatrist", audience: "psychiatrist" },
  clinic: { label: "Clinic manager", audience: "clinic" },
};

const TIER_LABEL = { SOLO: "Individual Practitioner", CLINIC_LIGHT: "Clinic Light", CLINIC_PLUS: "Clinic Plus" };

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
}

async function jsonOrThrow(res) {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw body?.detail || body;
  return body;
}

function MasterViewBanner({ role, targets, targetId, onTargetChange, onExit, onEnterRealView, entering }) {
  return (
    <div className="sticky top-0 z-40 border-b border-amber-300 bg-amber-50">
      <div className="mx-auto flex max-w-[1500px] flex-wrap items-center gap-3 px-4 py-2.5 sm:px-6 lg:px-8">
        <ShieldAlert className="h-5 w-5 shrink-0 text-amber-700" />
        <div className="text-sm text-amber-900">
            <span className="font-semibold">Master View (Admin) — TEST ENVIRONMENT</span>{" "}
            <span>Viewing SimplyRefer as your dedicated{" "}</span>
            <span className="font-semibold">{ROLE_CONFIG[role]?.label}</span>{" "}
            <span>QA test account. Real clinician, GP and clinic data is never shown here — changes you make (e.g. availability) save only to this test account.</span>
          </div>
        {targets.length > 0 && (
          <select
            className="ml-auto rounded-md border border-amber-300 bg-white px-2 py-1 text-xs text-slate-700"
            value={targetId}
            onChange={(e) => onTargetChange(e.target.value)}
            data-testid="master-view-target-picker"
          >
            {targets.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
        )}
        <Button size="sm" className="bg-navy-900 text-white hover:bg-navy-800" onClick={onEnterRealView} disabled={entering || !targetId} data-testid="master-view-enter-real">
          <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> {entering ? "Opening real view…" : "Open Real View"}
        </Button>
        <Button size="sm" variant="outline" className="border-amber-400 bg-white" onClick={onExit} data-testid="master-view-exit">
          <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Exit View
        </Button>
      </div>
    </div>
  );
}

function RealDataCard({ title, children }) {
  return (
    <section className="rounded-lg border border-sky-200 bg-sky-50/50 p-5">
      <div className="mb-3 flex items-center gap-2 text-sky-800">
        <ShieldCheck className="h-4 w-4" />
        <span className="text-xs font-semibold uppercase tracking-wide">QA test account — dedicated, editable</span>
      </div>
      <h3 className="mb-3 font-heading text-lg font-semibold text-slate-900">{title}</h3>
      {children}
    </section>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm text-slate-900">{value ?? "—"}</p>
    </div>
  );
}

function LocalAvailabilityPicker({ role, targetId, initial }) {
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const opt = AVAIL_OPTIONS.find((o) => o.value === value) || AVAIL_OPTIONS[0];
  const onPick = async (next) => {
    const prev = value;
    setValue(next);
    setSaving(true);
    try {
      const res = await authedFetch("/admin/view-as/availability", {
        method: "PATCH",
        body: JSON.stringify({ role, target_id: targetId, availability_status: next }),
      });
      if (!res.ok) throw new Error(await errText(res));
      toast.success("Saved to the QA test account.");
    } catch (e) {
      setValue(prev);
      toast.error(e.message || "Could not save availability.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Availability (saves to the QA test account)</p>
      <select
        disabled={saving}
        className="mt-1 rounded-md border px-2 py-1.5 text-sm"
        style={{ backgroundColor: opt.style.backgroundColor, color: opt.style.color, borderColor: opt.style.borderColor }}
        value={value}
        onChange={(e) => onPick(e.target.value)}
        data-testid="master-view-availability-select"
      >
        {AVAIL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function TeamList({ team }) {
  if (!team?.length) return <p className="text-sm text-slate-500">No clinicians on this account yet.</p>;
  return (
    <ul className="divide-y divide-slate-100">
      {team.map((m) => (
        <li key={m.id} className="flex items-center justify-between py-2 text-sm">
          <span className="text-slate-900">{m.first_name} {m.last_name} <span className="text-slate-400">· {m.profession}</span></span>
          <span className="flex items-center gap-1 text-xs text-slate-500">
            {m.is_verified && <BadgeCheck className="h-3.5 w-3.5 text-emerald-600" />}
            {(AVAIL_OPTIONS.find((o) => o.value === m.availability_status) || {}).label || m.availability_status}
          </span>
        </li>
      ))}
    </ul>
  );
}

function MasterViewBillingPanel({ role, targetId, defaultTier }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState("");
  const [tier, setTier] = useState(defaultTier);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    try {
      const res = await authedFetch(`/admin/view-as/billing/state?role=${role}&target_id=${targetId}`);
      const body = await jsonOrThrow(res);
      setState(body);
      setTier(body.tier || defaultTier);
    } catch (e) {
      toast.error(errText(e, "Couldn't load the Master View billing sandbox."));
    }
  };

  useEffect(() => { if (targetId) load(); /* eslint-disable-next-line */ }, [role, targetId]);

  useEffect(() => {
    const setupSid = searchParams.get("mv_setup_session_id");
    const checkoutSid = searchParams.get("mv_checkout_session_id");
    if (!setupSid && !checkoutSid) return;
    (async () => {
      try {
        if (setupSid) {
          await jsonOrThrow(await authedFetch(`/admin/view-as/billing/setup-status/${setupSid}?role=${role}&target_id=${targetId}`));
          toast.success("Test card added to the Master View sandbox.");
        }
        if (checkoutSid) {
          await jsonOrThrow(await authedFetch(`/admin/view-as/billing/status/${checkoutSid}?role=${role}&target_id=${targetId}`));
          toast.success("Master View sandbox subscription updated.");
        }
      } catch (e) {
        toast.error(errText(e, "Couldn't confirm the Stripe test session."));
      } finally {
        const next = new URLSearchParams(searchParams);
        next.delete("mv_setup_session_id"); next.delete("mv_checkout_session_id");
        setSearchParams(next, { replace: true });
        load();
      }
    })();
    // eslint-disable-next-line
  }, []);

  const act = async (key, path, body) => {
    setBusy(key);
    try {
      const res = await authedFetch(path, { method: "POST", body: JSON.stringify(body || {}) });
      const out = await jsonOrThrow(res);
      if (out.url) { window.location.href = out.url; return; }
      toast.success("Done.");
      await load();
    } catch (e) {
      toast.error(errText(e, "That Master View billing action failed."));
    } finally {
      setBusy("");
    }
  };

  return (
    <section className="rounded-lg border border-violet-200 bg-violet-50/50 p-5" data-testid="master-view-billing-panel">
      <div className="mb-3 flex items-center gap-2 text-violet-800">
        <CreditCard className="h-4 w-4" />
        <span className="text-xs font-semibold uppercase tracking-wide">Master View Stripe sandbox (test mode) — safe to interact with</span>
      </div>
      {!state ? (
        <p className="text-sm text-slate-500">Loading sandbox…</p>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Sandbox plan" value={TIER_LABEL[state.tier] || state.tier} />
            <Field label="Sandbox status" value={state.status} />
            <Field label="Test card on file" value={state.payment_method_ready ? "Yes" : "No"} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select className="rounded-md border px-2 py-1.5 text-sm" value={tier} onChange={(e) => setTier(e.target.value)} data-testid="master-view-tier-select">
              {Object.keys(TIER_LABEL).map((k) => <option key={k} value={k}>{TIER_LABEL[k]}</option>)}
            </select>
            <Button size="sm" disabled={!!busy} onClick={() => act("setup", "/admin/view-as/billing/setup", { role, target_id: targetId })}>
              {busy === "setup" ? "Opening…" : "Add test card"}
            </Button>
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => act("checkout", "/admin/view-as/billing/checkout", { role, target_id: targetId, tier })}>
              {busy === "checkout" ? "Opening…" : (state.has_subscription ? "Change plan" : "Start subscription")}
            </Button>
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => act("portal", "/admin/view-as/billing/portal", { role, target_id: targetId })}>
              {busy === "portal" ? "Opening…" : "Manage billing"} <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
            </Button>
            {state.has_subscription && (
              <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => act("cancel", "/admin/view-as/billing/cancel", { role, target_id: targetId })}>
                {busy === "cancel" ? "Cancelling…" : "Cancel plan"}
              </Button>
            )}
            <Button
              size="sm" variant="ghost" className="ml-auto text-slate-500" disabled={!!busy}
              onClick={async () => {
                if (!window.confirm("Reset the Master View sandbox? This clears the sandbox test customer/subscription so you can test signup from scratch. It never touches real billing.")) return;
                await act("reset", "/admin/view-as/billing/reset", { role, target_id: targetId });
              }}
            >
              {busy === "reset" ? "Resetting…" : <><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Reset sandbox</>}
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            This uses SimplyRefer's dedicated Master View Stripe test-mode account and dedicated test products — it can never
            reach live Stripe or the real account's subscription. Use Stripe's test card 4242 4242 4242 4242, any future
            expiry, any CVC.
          </p>
        </div>
      )}
    </section>
  );
}

export default function AdminPreviewPortal() {
  const { previewRole } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const config = ROLE_CONFIG[previewRole];

  const [targets, setTargets] = useState([]);
  const [targetId, setTargetIdState] = useState(searchParams.get("target_id") || "");
  const [enteringReal, setEnteringReal] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const setTargetId = (id) => {
    setTargetIdState(id);
    const next = new URLSearchParams(searchParams);
    next.set("target_id", id);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (!config) return;
    setData(null);
    (async () => {
      try {
        const res = await authedFetch(`/admin/view-as/targets/${previewRole}`);
        const body = await jsonOrThrow(res);
        setTargets(body.targets || []);
        if (!targetId && body.targets?.length) setTargetId(body.targets[0].id);
      } catch (e) {
        toast.error(errText(e, "Couldn't load real accounts for this role."));
      }
    })();
    // eslint-disable-next-line
  }, [previewRole]);

  useEffect(() => {
    if (!config || previewRole === "gp" || !targetId) { setLoading(false); return; }
    setLoading(true);
    (async () => {
      try {
        const res = await authedFetch(`/admin/view-as/data/${previewRole}/${targetId}`);
        setData(await jsonOrThrow(res));
      } catch (e) {
        toast.error(errText(e, "Couldn't load real data for this account."));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [previewRole, targetId]);

  if (!config) return <Navigate to="/admin" replace />;

  const enterRealView = async () => {
    if (!targetId || enteringReal) return;
    setEnteringReal(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData?.session) {
        sessionStorage.setItem("sr_admin_session", JSON.stringify({
          access_token: sessionData.session.access_token,
          refresh_token: sessionData.session.refresh_token,
        }));
      }
      const res = await authedFetch(`/admin/view-as/session?role=${previewRole}`, { method: "POST" });
      if (!res.ok) throw new Error(await errText(res));
      const payload = await res.json();
      const { error } = await supabase.auth.verifyOtp({ email: payload.email, token_hash: payload.token_hash, type: "email" });
      if (error) throw error;
      sessionStorage.setItem("sr_master_view_role", previewRole);
      const dest = previewRole === "gp" ? "/dashboard" : previewRole === "clinic" ? "/clinic" : "/clinician";
      window.location.href = dest;
    } catch (e) {
      toast.error(errText(e, "Couldn't open the real view."));
      setEnteringReal(false);
    }
  };
  const exitPreview = () => navigate("/admin");
  const defaultTier = previewRole === "clinic" ? "CLINIC_LIGHT" : "SOLO";

  return (
    <div className="min-h-screen bg-background">
      <MasterViewBanner role={previewRole} targets={targets} targetId={targetId} onTargetChange={setTargetId} onExit={exitPreview} onEnterRealView={enterRealView} entering={enteringReal} />
      <LaunchStatusBanner audience={config.audience} className="top-[45px]" />
      <main className="mx-auto max-w-[1500px] space-y-6 px-4 py-8 sm:px-6 lg:px-8" data-testid={`admin-view-as-${previewRole}`}>
        <div className="flex items-center gap-2 text-slate-500">
          <SRMark className="h-6 w-6" />
          <span className="text-sm">Master View</span>
        </div>

        {previewRole === "gp" && (
          <Clinicians
            adminRollupMode
            readOnlyMode
            embeddedMode
            viewAsMode
            demoFirstName="SimplyRefer Admin"
            demoScope={`admin-view:gp:${targetId || "none"}`}
            demoTrackEvent={() => {}}
          />
        )}

        {previewRole !== "gp" && loading && <p className="text-sm text-slate-500">Loading real account data…</p>}
        {previewRole !== "gp" && !loading && !targetId && (
          <p className="text-sm text-slate-500">No real {config.label.toLowerCase()} accounts exist yet to preview.</p>
        )}

        {previewRole !== "gp" && !loading && data && (previewRole === "psychologist" || previewRole === "psychiatrist") && (
          <>
            <RealDataCard title={`${data.profile?.first_name || ""} ${data.profile?.last_name || ""}`.trim() || "Clinician"}>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Profession" value={data.profile?.profession} />
                <Field label="Specialty" value={data.profile?.specialty} />
                <Field label="Suburb / state" value={[data.profile?.suburb, data.profile?.state].filter(Boolean).join(", ")} />
                <Field label="Verified" value={data.profile?.is_verified ? "Yes" : "Pending"} />
                <Field label="Plan" value={data.account ? (TIER_LABEL[data.account.subscription_tier] || data.account.subscription_tier) : "—"} />
                <Field label="Subscription status" value={data.account?.subscription_status} />
                <Field label="Trial ends" value={fmtDate(data.account?.trial_ends_at)} />
                <Field label="Payment method on file" value={data.account?.billing_payment_method_ready ? "Yes" : "No"} />
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                <LocalAvailabilityPicker role={previewRole} targetId={targetId} initial={data.profile?.availability_status} />
                <Field label="Search appearances" value={data.insights?.search_appearances} />
                <Field label="Profile views" value={data.insights?.profile_views} />
                <Field label="Selected for referral" value={data.insights?.selected_for_referral} />
              </div>
              {data.team?.length > 1 && (
                <div className="mt-4">
                  <p className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-500"><Users className="h-3.5 w-3.5" /> Clinic team</p>
                  <TeamList team={data.team} />
                </div>
              )}
            </RealDataCard>
            <MasterViewBillingPanel role={previewRole} targetId={targetId} defaultTier={defaultTier} />
          </>
        )}

        {previewRole !== "gp" && !loading && data && previewRole === "clinic" && (
          <>
            <RealDataCard title={data.account?.account_name || "Clinic"}>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Plan" value={TIER_LABEL[data.account?.subscription_tier] || data.account?.subscription_tier} />
                <Field label="Subscription status" value={data.account?.subscription_status} />
                <Field label="Trial ends" value={fmtDate(data.account?.trial_ends_at)} />
                <Field label="Payment method on file" value={data.account?.billing_payment_method_ready ? "Yes" : "No"} />
                <Field label="Billing period" value={data.account?.billing_period} />
                <Field label="Pending invites" value={data.invites?.filter((i) => i.status === "PENDING").length ?? 0} />
              </div>
              <div className="mt-4">
                <p className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-500"><Users className="h-3.5 w-3.5" /> Team ({data.team?.length || 0})</p>
                <TeamList team={data.team} />
              </div>
            </RealDataCard>
            <MasterViewBillingPanel role={previewRole} targetId={targetId} defaultTier={defaultTier} />
          </>
        )}
      </main>
    </div>
  );
}
